import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { AnimatedScreen } from 'Shared/components';
import { useTimelineData } from './useTimelineData';
import {
  VERSION_TYPE_LABELS,
  DraftStatusCode,
  VersionType,
  TimelineDraft,
  TimelineVersion,
  TimelineTerm,
  selectCreatedBy,
} from './types';
import { usePolicyContext } from 'Policies/utils';
import { useLazyApi } from 'Shared/services/Api';
import { useAuth0 } from 'Auth';
import { VersionDiffModal } from './VersionDiffModal';
import { RollbackConfirmationModal } from './RollbackConfirmationModal';
import { RollbackModalState, RollbackResponseType } from './rollbackTypes';
import styles from './styles.module.css';

function formatDate(isoString: string, timeZone?: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    }).format(parseISO(isoString));
  } catch {
    return isoString;
  }
}

function formatRelativeDate(isoString: string): string {
  try {
    return formatDistanceToNow(parseISO(isoString), { addSuffix: true });
  } catch {
    return isoString;
  }
}

function getTermStatusClass(termStatus: string): string {
  switch (termStatus.toLowerCase()) {
    case 'active':
      return styles.termStatusActive;
    case 'cancelled':
      return styles.termStatusCancelled;
    default:
      return styles.termStatusDefault;
  }
}

function getDraftStatusClass(status: DraftStatusCode): string {
  switch (status) {
    case 'PENDING':
      return styles.draftStatusPending;
    case 'APPLIED':
      return styles.draftStatusApplied;
    case 'TERMINATED':
      return styles.draftStatusTerminated;
  }
}

function getVersionDotClass(version: TimelineVersion): string {
  if (version.status.toUpperCase() === 'OBSOLETE') {
    return styles.versionDotObsolete;
  }
  switch (version.versionType.toUpperCase()) {
    case 'CANCELLATION':
      return styles.versionDotCancelled;
    default:
      return styles.versionDotActive;
  }
}

function isObsoleteVersion(version: TimelineVersion): boolean {
  return version.status.toUpperCase() === 'OBSOLETE';
}

type DiffModalParams = {
  versionId: string;
  versionType: VersionType;
  source: 'version' | 'draft';
  createdBy: string;
  createdAt?: string;
  versionNumber?: number;
  effectiveStart?: string;
  isObsolete?: boolean;
};

// ─── DraftItem ────────────────────────────────────────────────────────────────

type DraftItemProps = {
  draft: TimelineDraft;
  timeZone?: string;
  onOpenDiff: (params: DiffModalParams) => void;
  isClearcoverUser: boolean;
};

const DraftItem: FC<DraftItemProps> = ({
  draft,
  timeZone,
  onOpenDiff,
  isClearcoverUser,
}) => {
  const history = useHistory();
  const { id: policyId } = useParams<{ id: string }>();
  // Mirror PendingDraft.tsx: the Finalize affordance is only meaningful
  // for mid_term and renewal_quote drafts that are still PENDING — APPLIED
  // / TERMINATED drafts have nothing left to finalize.
  const canFinalize =
    draft.status === 'PENDING' &&
    (draft.draftType === 'mid_term' || draft.draftType === 'renewal_quote');

  return (
    <div key={draft.id} className={styles.versionDraftItem}>
      <div className={styles.versionMain}>
        <div className={styles.versionTitleRow}>
          <div className={styles.versionTitleGroup}>
            <div className={styles.versionTitle}>
              {VERSION_TYPE_LABELS[draft.draftType]}
            </div>
            <span className={getDraftStatusClass(draft.status)}>
              {draft.status}
            </span>
          </div>
          <div className={styles.draftActions}>
            {canFinalize && (
              <button
                className={styles.detailsButton}
                data-testid="timeline-finalize-draft-button"
                onClick={(e) => {
                  e.stopPropagation();
                  history.push(`/policies/${policyId}/draft/${draft.id}`);
                }}
              >
                Finalize Draft
              </button>
            )}
            {isClearcoverUser && (
              <button
                className={styles.detailsButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenDiff({
                    versionId: draft.id,
                    versionType: draft.draftType,
                    source: 'draft',
                    createdBy: draft.createdBy,
                    createdAt: draft.createdAt,
                  });
                }}
              >
                View Draft Changes
              </button>
            )}
          </div>
        </div>
        {draft.effectiveDate && (
          <div className={styles.versionDate}>
            Effective: {formatDate(draft.effectiveDate, timeZone)}
          </div>
        )}
        <div
          className={styles.versionMeta}
          title={formatRelativeDate(draft.createdAt)}
        >
          Created by {selectCreatedBy(draft.createdBy)} &bull;{' '}
          {formatDate(draft.createdAt, timeZone)}
        </div>
      </div>
    </div>
  );
};

// ─── VersionItem ──────────────────────────────────────────────────────────────

type VersionItemProps = {
  version: TimelineVersion;
  versionEndDate?: string;
  timeZone?: string;
  draftsExpanded: boolean;
  onToggleDrafts: () => void;
  onOpenDiff: (params: DiffModalParams) => void;
  onOpenRollbackModal: (version: TimelineVersion) => void;
  isCurrent: boolean;
  isCurrentTerm: boolean;
  isClearcoverUser: boolean;
  isPolicyCancelled: boolean;
};

const VersionItem: FC<VersionItemProps> = ({
  version,
  versionEndDate,
  timeZone,
  draftsExpanded,
  onToggleDrafts,
  onOpenDiff,
  onOpenRollbackModal,
  isCurrent,
  isCurrentTerm,
  isClearcoverUser,
  isPolicyCancelled,
}) => {
  const pendingDrafts = version.drafts.filter((d) => d.status === 'PENDING');
  const isObsolete = isObsoleteVersion(version);

  const canRollback =
    isCurrentTerm &&
    !isCurrent &&
    version.versionType !== 'cancellation' &&
    !isObsolete;

  const rowClasses = [
    styles.versionItem,
    isObsolete ? styles.versionItemRolledBack : '',
    pendingDrafts.length > 0 ? styles.versionItemExpandable : '',
  ]
    .filter(Boolean)
    .join(' ');

  const titleClass = isObsolete
    ? styles.versionTitleObsolete
    : styles.versionTitle;

  return (
    <div className={styles.versionItemGroup}>
      <div
        className={rowClasses}
        onClick={pendingDrafts.length > 0 ? onToggleDrafts : undefined}
      >
        {/* Circle node — the 4px outline ring "cuts" the vertical spine */}
        <div
          className={getVersionDotClass(version)}
          aria-label={`Version ${version.versionNumber} — ${version.status.toUpperCase()}`}
        >
          {version.versionNumber}
        </div>

        <div className={styles.versionMain}>
          <div className={styles.versionTitleRow}>
            <div className={styles.versionTitleGroup}>
              <div className={titleClass}>
                {VERSION_TYPE_LABELS[version.versionType]}
              </div>
              {isCurrent && (
                <span className={styles.currentBadge}>Current</span>
              )}
              {isObsolete && (
                /* ↺ U+21BA ANTICLOCKWISE OPEN CIRCLE ARROW */
                <span className={styles.rolledBackBadge}>
                  &#x21BA; Rolled Back
                </span>
              )}
            </div>

            <div className={styles.versionItemActions}>
              {isClearcoverUser && (
                <button
                  className={styles.detailsButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenDiff({
                      versionId: version.id,
                      versionType: version.versionType,
                      source: 'version',
                      createdBy: version.createdBy,
                      createdAt: version.createdAt,
                      versionNumber: version.versionNumber,
                      effectiveStart: version.versionStartDate,
                      isObsolete: version.status.toUpperCase() === 'OBSOLETE',
                    });
                  }}
                >
                  View Changes
                </button>
              )}

              {/*
               * Rollback affordance: show a separator + button when:
               *   - canRollback (enabled), OR
               *   - isCurrent and not a cancellation (disabled — "Already current"), OR
               *   - isCurrentTerm + cancellation + not obsolete (disabled).
               * Entirely hidden on rolled-back (OBSOLETE) rows and when
               * !isClearcoverUser or isPolicyCancelled (AC08/AC16/AC20).
               */}
              {isClearcoverUser &&
                !isPolicyCancelled &&
                !isObsolete &&
                (canRollback ||
                  (isCurrent && version.versionType !== 'cancellation') ||
                  (isCurrentTerm &&
                    version.versionType === 'cancellation' &&
                    !isObsolete)) && (
                  <>
                    <span
                      className={styles.actionSeparator}
                      aria-hidden="true"
                    />
                    <button
                      className={
                        isCurrent || version.versionType === 'cancellation'
                          ? styles.rollbackLinkDisabled
                          : styles.rollbackLink
                      }
                      disabled={
                        isCurrent || version.versionType === 'cancellation'
                      }
                      onClick={
                        canRollback
                          ? (e) => {
                              e.stopPropagation();
                              onOpenRollbackModal(version);
                            }
                          : undefined
                      }
                      title={
                        version.versionType === 'cancellation'
                          ? 'Cannot roll back to a cancelled version'
                          : isCurrent
                            ? 'Already the current version'
                            : 'Rollback policy to this version'
                      }
                    >
                      Rollback to this
                    </button>
                  </>
                )}

              {pendingDrafts.length > 0 && (
                <button
                  className={styles.versionDraftsChevronButton}
                  aria-expanded={draftsExpanded}
                  aria-label={
                    draftsExpanded
                      ? 'Hide pending drafts'
                      : 'Show pending drafts'
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleDrafts();
                  }}
                >
                  <span className={styles.pendingDraftsChip}>
                    Pending drafts: {pendingDrafts.length}
                  </span>
                  <span
                    className={
                      draftsExpanded
                        ? styles.accordionIconExpanded
                        : styles.accordionIcon
                    }
                    aria-hidden="true"
                  />
                </button>
              )}
            </div>
          </div>

          <div className={styles.versionDate}>
            Effective:{' '}
            {format(parseISO(version.versionStartDate), 'MMM d, yyyy')}
            {versionEndDate &&
              ` – ${format(parseISO(versionEndDate), 'MMM d, yyyy')}`}
          </div>
          <div
            className={styles.versionMeta}
            title={formatRelativeDate(version.createdAt)}
          >
            Created by {selectCreatedBy(version.createdBy)} &bull;{' '}
            {formatDate(version.createdAt, timeZone)}
          </div>
        </div>
      </div>

      {draftsExpanded && pendingDrafts.length > 0 && (
        <div className={styles.versionDraftsList}>
          {pendingDrafts.map((draft) => (
            <DraftItem
              key={draft.id}
              draft={draft}
              timeZone={timeZone}
              onOpenDiff={onOpenDiff}
              isClearcoverUser={isClearcoverUser}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── TermSection ──────────────────────────────────────────────────────────────

type TermSectionProps = {
  term: TimelineTerm;
  timeZone?: string;
  isCollapsed: boolean;
  onToggle: () => void;
  expandedVersionDrafts: Record<string, boolean>;
  onToggleVersionDrafts: (versionId: string) => void;
  onOpenDiff: (params: DiffModalParams) => void;
  onOpenRollbackModal: (version: TimelineVersion) => void;
  isCurrentTerm: boolean;
  isClearcoverUser: boolean;
  isPolicyCancelled: boolean;
};

type VersionSegment =
  | { type: 'version'; version: TimelineVersion }
  | { type: 'obsolete-group'; versions: TimelineVersion[]; groupIndex: number };

function buildVersionSegments(versions: TimelineVersion[]): VersionSegment[] {
  const sorted = [...versions].sort(
    (a, b) => b.versionNumber - a.versionNumber
  );
  const segments: VersionSegment[] = [];
  let pending: TimelineVersion[] = [];
  let groupCount = 0;

  for (const version of sorted) {
    if (version.status.toUpperCase() === 'OBSOLETE') {
      pending.push(version);
    } else {
      if (pending.length > 0) {
        segments.push({
          type: 'obsolete-group',
          versions: pending,
          groupIndex: groupCount++,
        });
        pending = [];
      }
      segments.push({ type: 'version', version });
    }
  }
  if (pending.length > 0) {
    segments.push({
      type: 'obsolete-group',
      versions: pending,
      groupIndex: groupCount++,
    });
  }
  return segments;
}

const TermSection: FC<TermSectionProps> = ({
  term,
  timeZone,
  isCollapsed,
  onToggle,
  expandedVersionDrafts,
  onToggleVersionDrafts,
  onOpenDiff,
  onOpenRollbackModal,
  isCurrentTerm,
  isClearcoverUser,
  isPolicyCancelled,
}) => {
  const [expandedObsoleteGroups, setExpandedObsoleteGroups] = useState<
    Record<number, boolean>
  >({});

  const maxVersionNumber = isCurrentTerm
    ? Math.max(...term.versions.map((v) => v.versionNumber))
    : -Infinity;

  // Build a map of versionId → end date (start of next version, or term end for latest)
  const sortedVersions = [...term.versions].sort(
    (a, b) => a.versionNumber - b.versionNumber
  );
  const versionEndDates: Record<string, string> = {};
  sortedVersions.forEach((v, i) => {
    versionEndDates[v.id] =
      i < sortedVersions.length - 1
        ? sortedVersions[i + 1].versionStartDate
        : term.effectivePeriod.endAt;
  });

  const segments = buildVersionSegments(term.versions);

  return (
    <div className={styles.termSection}>
      {/*
       * Term header bar — clicking anywhere on it toggles the version list.
       * The right side shows a static status pill.
       * TODO DLC-590: wire up the status pill as a filter dropdown.
       */}
      <button
        data-testid={`term-accordion-${term.termNumber}`}
        className={styles.accordionHeader}
        aria-expanded={!isCollapsed}
        onClick={onToggle}
      >
        <div className={styles.accordionHeaderLeft}>
          <div className={styles.termTitleGroup}>
            <h3>Term {term.termNumber}</h3>
            {isCurrentTerm && (
              <span className={styles.termCurrentBadge}>Current</span>
            )}
          </div>
          <p className={styles.effectivePeriod}>
            {formatDate(term.effectivePeriod.startAt, timeZone)} to{' '}
            {formatDate(term.effectivePeriod.endAt, timeZone)}
          </p>
        </div>
        <div className={styles.accordionHeaderRight}>
          {/* Static status pill — TODO DLC-590: add dropdown filter */}
          <span className={getTermStatusClass(term.termStatus)}>
            {term.termStatus}
          </span>
          <span
            className={
              isCollapsed ? styles.accordionIcon : styles.accordionIconExpanded
            }
            aria-hidden="true"
          />
        </div>
      </button>

      {!isCollapsed && (
        <div className={styles.versionsContainer}>
          {term.versions.length === 0 ? (
            <div className={styles.noVersions}>No versions</div>
          ) : (
            segments.map((segment) => {
              if (segment.type === 'version') {
                const { version } = segment;
                return (
                  <VersionItem
                    key={version.id}
                    version={version}
                    versionEndDate={versionEndDates[version.id]}
                    timeZone={timeZone}
                    draftsExpanded={expandedVersionDrafts[version.id] || false}
                    onToggleDrafts={() => onToggleVersionDrafts(version.id)}
                    onOpenDiff={onOpenDiff}
                    onOpenRollbackModal={onOpenRollbackModal}
                    isCurrent={
                      isCurrentTerm &&
                      version.versionNumber === maxVersionNumber
                    }
                    isCurrentTerm={isCurrentTerm}
                    isClearcoverUser={isClearcoverUser}
                    isPolicyCancelled={isPolicyCancelled}
                  />
                );
              }

              const { versions: obsoleteVersions, groupIndex } = segment;
              const isExpanded = expandedObsoleteGroups[groupIndex] || false;
              const count = obsoleteVersions.length;
              return (
                <React.Fragment key={`obsolete-group-${groupIndex}`}>
                  {/*
                   * The toggle row has solid white bg + z-index:1 to break
                   * the vertical spine visually at this insertion point.
                   */}
                  <div className={styles.obsoleteGroupRow}>
                    <button
                      className={styles.obsoleteGroupToggle}
                      aria-expanded={isExpanded}
                      onClick={() =>
                        setExpandedObsoleteGroups((prev) => ({
                          ...prev,
                          [groupIndex]: !isExpanded,
                        }))
                      }
                    >
                      <span
                        className={
                          isExpanded
                            ? styles.accordionIconExpanded
                            : styles.accordionIcon
                        }
                        aria-hidden="true"
                      />
                      {isExpanded ? 'Hide' : 'Show'} rolled back versions (
                      {count})
                    </button>
                  </div>
                  {isExpanded &&
                    obsoleteVersions.map((version) => (
                      <VersionItem
                        key={version.id}
                        version={version}
                        versionEndDate={versionEndDates[version.id]}
                        timeZone={timeZone}
                        draftsExpanded={
                          expandedVersionDrafts[version.id] || false
                        }
                        onToggleDrafts={() => onToggleVersionDrafts(version.id)}
                        onOpenDiff={onOpenDiff}
                        onOpenRollbackModal={onOpenRollbackModal}
                        isCurrent={false}
                        isCurrentTerm={isCurrentTerm}
                        isClearcoverUser={isClearcoverUser}
                        isPolicyCancelled={isPolicyCancelled}
                      />
                    ))}
                </React.Fragment>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ─── PolicyTimeline (root) ────────────────────────────────────────────────────

type PolicyTimelineProps = {
  onShowActivities: () => void;
  onLoadingChange?: (loading: boolean) => void;
};

export const PolicyTimeline: FC<PolicyTimelineProps> = ({
  onShowActivities,
  onLoadingChange,
}) => {
  const { id: policyId } = useParams<{ id: string }>();
  const history = useHistory();
  const { timeline, loading, error } = useTimelineData(policyId);
  const { user } = useAuth0();
  const isClearcoverUser = !!user?.isInternalUser;

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);
  const timeZone = usePolicyContext().details.timeZone;

  const [collapsedTerms, setCollapsedTerms] = useState<Record<string, boolean>>(
    {}
  );
  const [expandedVersionDrafts, setExpandedVersionDrafts] = useState<
    Record<string, boolean>
  >({});
  const [skipObsolete, setSkipObsolete] = useState(true);
  const [diffModal, setDiffModal] = useState<
    ({ open: boolean; navIndex: number } & DiffModalParams) | null
  >(null);
  const [rollbackModal, setRollbackModal] = useState<RollbackModalState | null>(
    null
  );
  const diffTriggerRef = useRef<Element | null>(null);

  const [, executeRollback] = useLazyApi<RollbackResponseType>({
    method: 'POST',
    url: `/policies/${policyId}/rollback`,
  });

  const allVersions = useMemo(
    () =>
      [...timeline]
        .sort((a, b) => b.termNumber - a.termNumber)
        .flatMap((term) =>
          [...term.versions].sort((a, b) => b.versionNumber - a.versionNumber)
        ),
    [timeline]
  );

  const termNumberById = useMemo(
    () =>
      timeline.reduce(
        (acc, term) => {
          acc[term.id] = term.termNumber;
          return acc;
        },
        {} as Record<string, number>
      ),
    [timeline]
  );

  const hasObsoleteVersions = useMemo(
    () => allVersions.some(isObsoleteVersion),
    [allVersions]
  );

  const navPageItems = useMemo(
    () =>
      allVersions.map((version, index) => ({
        position: allVersions.length - index,
        isObsolete: isObsoleteVersion(version),
        label: `${termNumberById[version.policyTermId]}.${
          version.versionNumber
        }`,
      })),
    [allVersions, termNumberById]
  );

  const getNavigableNavIndex = useCallback(
    (currentIndex: number, direction: 1 | -1) => {
      const adjacentIndex = currentIndex + direction;

      if (adjacentIndex < 0 || adjacentIndex >= allVersions.length) {
        return -1;
      }

      if (!skipObsolete) {
        return adjacentIndex;
      }

      for (
        let index = adjacentIndex;
        index >= 0 && index < allVersions.length;
        index += direction
      ) {
        if (!isObsoleteVersion(allVersions[index])) {
          return index;
        }
      }

      return -1;
    },
    [allVersions, skipObsolete]
  );

  const prevNavIndex =
    diffModal && diffModal.navIndex >= 0
      ? getNavigableNavIndex(diffModal.navIndex, 1)
      : -1;
  const nextNavIndex =
    diffModal && diffModal.navIndex >= 0
      ? getNavigableNavIndex(diffModal.navIndex, -1)
      : -1;

  const isPrevAtBoundary =
    !!diffModal &&
    diffModal.navIndex >= 0 &&
    diffModal.navIndex === allVersions.length - 1;
  const isNextAtBoundary =
    !!diffModal && diffModal.navIndex >= 0 && diffModal.navIndex === 0;

  const handleOpenDiff = (params: DiffModalParams) => {
    diffTriggerRef.current = document.activeElement;
    const navIndex =
      params.source === 'version'
        ? allVersions.findIndex((v) => v.id === params.versionId)
        : -1;
    setDiffModal({ open: true, ...params, navIndex });
  };

  const handleCloseDiff = () => {
    setDiffModal(null);
    (diffTriggerRef.current as HTMLElement | null)?.focus();
    diffTriggerRef.current = null;
  };

  const handleNavVersion = (index: number) => {
    const version = allVersions[index];
    if (!version) return;
    setDiffModal({
      open: true,
      versionId: version.id,
      versionType: version.versionType,
      source: 'version',
      createdBy: version.createdBy,
      createdAt: version.createdAt,
      versionNumber: version.versionNumber,
      effectiveStart: version.versionStartDate,
      isObsolete: isObsoleteVersion(version),
      navIndex: index,
    });
  };

  const handleToggleVersionDrafts = (versionId: string) =>
    setExpandedVersionDrafts((current) => ({
      ...current,
      [versionId]: !current[versionId],
    }));

  const handleOpenRollbackModal = (version: TimelineVersion) => {
    setRollbackModal({
      open: true,
      versionId: version.id,
      versionNumber: version.versionNumber,
      isSubmitting: false,
    });
  };

  const handleConfirmRollback = () => {
    if (!rollbackModal) return;

    setRollbackModal((prev) => (prev ? { ...prev, isSubmitting: true } : null));

    executeRollback({ data: { baseVersionId: rollbackModal.versionId } })
      .then((response) => {
        setRollbackModal(null);
        history.push(`/policies/${policyId}/draft/${response.data.draftId}`);
      })
      .catch((err) => {
        setRollbackModal((prev) =>
          prev
            ? {
                ...prev,
                isSubmitting: false,
                error: err?.message || 'Failed to create rollback draft',
              }
            : null
        );
      });
  };

  const handleCloseRollbackModal = () => setRollbackModal(null);

  const currentTermId = timeline.length
    ? ((
        [...timeline]
          .filter((t) => t.termStatus.toLowerCase() === 'active')
          .sort((a, b) => b.termNumber - a.termNumber)[0] ??
        [...timeline].sort((a, b) => b.termNumber - a.termNumber)[0]
      )?.id ?? null)
    : null;
  const isPolicyCancelled =
    timeline.find((t) => t.id === currentTermId)?.termStatus.toLowerCase() ===
    'cancelled';

  const collapsedInitialized = useRef(false);
  useEffect(() => {
    if (timeline.length === 0 || collapsedInitialized.current) return;
    collapsedInitialized.current = true;
    const mostRecentTermId =
      currentTermId ??
      [...timeline].sort((a, b) => b.termNumber - a.termNumber)[0]?.id;
    setCollapsedTerms(
      timeline.reduce(
        (acc, term) => {
          acc[term.id] = term.id !== mostRecentTermId;
          return acc;
        },
        {} as Record<string, boolean>
      )
    );

    const currentTerm = timeline.find((t) => t.id === mostRecentTermId);
    if (currentTerm && currentTerm.versions.length > 0) {
      const currentVersion = currentTerm.versions.reduce((a, b) =>
        a.versionNumber > b.versionNumber ? a : b
      );
      setExpandedVersionDrafts({ [currentVersion.id]: true });
    }
  }, [timeline, currentTermId]);

  return (
    <>
      <div
        id="policy-timeline-card"
        data-testid="policy-timeline-card"
        className={styles.timelineCard}
      >
        <AnimatedScreen loading={loading}>
          <div className={styles.timelineContent}>
            {error ? (
              <div className={styles.timelineError}>
                <p>Unable to load timeline data.</p>
                <button
                  className={styles.timelineErrorAction}
                  onClick={onShowActivities}
                >
                  View Activities instead
                </button>
              </div>
            ) : !timeline || timeline.length === 0 ? (
              <div className={styles.emptyState}>No timeline data found</div>
            ) : (
              [...timeline]
                .sort((a, b) => b.termNumber - a.termNumber)
                .map((term) => (
                  <TermSection
                    key={term.id}
                    term={term}
                    timeZone={timeZone}
                    isCollapsed={collapsedTerms[term.id] ?? true}
                    onToggle={() =>
                      setCollapsedTerms((current) => ({
                        ...current,
                        [term.id]: !(current[term.id] ?? true),
                      }))
                    }
                    expandedVersionDrafts={expandedVersionDrafts}
                    onToggleVersionDrafts={handleToggleVersionDrafts}
                    onOpenDiff={handleOpenDiff}
                    onOpenRollbackModal={handleOpenRollbackModal}
                    isCurrentTerm={term.id === currentTermId}
                    isClearcoverUser={isClearcoverUser}
                    isPolicyCancelled={isPolicyCancelled}
                  />
                ))
            )}
          </div>
        </AnimatedScreen>
      </div>
      {diffModal && (
        <VersionDiffModal
          policyId={policyId || ''}
          versionId={diffModal.versionId}
          versionType={diffModal.versionType}
          source={diffModal.source}
          createdBy={diffModal.createdBy}
          createdAt={diffModal.createdAt}
          versionNumber={diffModal.versionNumber}
          effectiveStart={diffModal.effectiveStart}
          isObsolete={diffModal.isObsolete}
          skipObsolete={skipObsolete}
          onToggleSkipObsolete={
            hasObsoleteVersions
              ? () => setSkipObsolete((current) => !current)
              : undefined
          }
          open={diffModal.open}
          onClose={handleCloseDiff}
          onPrev={
            diffModal.navIndex >= 0 && !isPrevAtBoundary && prevNavIndex >= 0
              ? () => handleNavVersion(prevNavIndex)
              : undefined
          }
          onNext={
            diffModal.navIndex >= 0 && !isNextAtBoundary && nextNavIndex >= 0
              ? () => handleNavVersion(nextNavIndex)
              : undefined
          }
          isPrevDisabled={diffModal.navIndex < 0 || isPrevAtBoundary}
          isNextDisabled={diffModal.navIndex < 0 || isNextAtBoundary}
          navPosition={
            diffModal.navIndex >= 0
              ? allVersions.length - diffModal.navIndex
              : undefined
          }
          navTotal={diffModal.navIndex >= 0 ? allVersions.length : undefined}
          navPageItems={diffModal.navIndex >= 0 ? navPageItems : undefined}
          onSelectNavPosition={
            diffModal.navIndex >= 0
              ? (position: number) => {
                  const index = allVersions.length - position;
                  handleNavVersion(index);
                }
              : undefined
          }
        />
      )}
      {rollbackModal && (
        <RollbackConfirmationModal
          open={rollbackModal.open}
          versionNumber={rollbackModal.versionNumber}
          onClose={handleCloseRollbackModal}
          onSubmit={handleConfirmRollback}
          isSubmitting={rollbackModal.isSubmitting}
          error={rollbackModal.error}
        />
      )}
    </>
  );
};
